import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from 'store';
import {
  GENERAL__COLLARTYPE,
  GENERAL__WORKERCLASS,
  PARITAIR_COMITE__END_DATE,
  PARITAIR_COMITE__EXPERIENCE_OR_AGE,
  PARITAIR_COMITE__START_DATE,
  WAGE_INDEXATION__NEW_VALUES,
} from 'translations/constants';
import { Box, Typography } from '@mui/material';
import { wageIndexationPreviewSelector } from 'store/selectors/wage-indexation-selectors';
import { useTranslation } from 'react-i18next';
import { toNumber } from 'lodash';
import { UPDataGrid } from 'components/data-grid';
import CATEGORIES from 'constants/Categories';
import { IMinimumWage } from 'types/minimum-wage';
import { WageIndexationHalfGridContainer } from './styles';
import { updatedMinimumWagesProps } from './types';

const IndexedWageValuesGrid: React.FC<updatedMinimumWagesProps> = ({ setUpdatedWages }) => {
  const [t] = useTranslation();

  const [rowOnHover, setRowOnHover] = useState<number>();
  const previewData = useAppSelector(wageIndexationPreviewSelector);

  const [wages, setWages] = useState<IMinimumWage[]>([]);

  useEffect(() => {
    if (previewData) {
      setWages(previewData);
      setUpdatedWages(previewData);
    }
  }, [previewData, setUpdatedWages]);

  const addOrUpdateEntity = useCallback(
    (entity: IMinimumWage, field: string, value: any): IMinimumWage => {
      const updatedEntity: IMinimumWage = {
        ...entity,
        [field]: value,
      };

      const swap = wages.map((item: IMinimumWage) => {
        if (item.id === updatedEntity.id) {
          return updatedEntity;
        }
        return item;
      });

      setWages(swap);
      setUpdatedWages(swap);
      return updatedEntity;
    },
    [wages, setUpdatedWages],
  );

  const getDataGridEditableColumns = useCallback((): GridColDef[] => {
    return CATEGORIES.map(
      (_, index: number): GridColDef => ({
        field: `category${index + 1}`,
        headerName: `Cat ${index + 1}`,
        width: 90,
        editable: true,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        valueSetter: params => {
          if (params.value !== '' && Number.isNaN(Number(params.value))) {
            return params.row;
          }
          const value = params.value === '' ? null : params.value;
          return addOrUpdateEntity(params.row as IMinimumWage, `category${index + 1}`, value);
        },
        preProcessEditCellProps: (params: any) => {
          const hasError = Number.isNaN(toNumber(params.props.value));
          return { ...params.props, error: hasError };
        },
      }),
    );
  }, [addOrUpdateEntity]);

  const workerclassColumns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    {
      field: 'yearsOfExperience',
      headerName: t(PARITAIR_COMITE__EXPERIENCE_OR_AGE),
      width: 160,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      sortingOrder: ['asc'],
    },
    {
      field: 'collarType',
      headerName: t(GENERAL__COLLARTYPE),
      width: 150,
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'workerclass',
      headerName: t(GENERAL__WORKERCLASS),
      width: 150,
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
    },
    ...getDataGridEditableColumns(),
    {
      field: 'startDate',
      headerName: t(PARITAIR_COMITE__START_DATE),
      width: 150,
      editable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Date>) => {
        if (params.value !== null) {
          const dateValue = new Date(params.value);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {dateValue.getDate()}-{dateValue.getMonth() + 1}-{dateValue.getFullYear()}
            </Box>
          );
        }
        return <Box />;
      },
      type: 'date',
    },
    {
      field: 'endDate',
      headerName: t(PARITAIR_COMITE__END_DATE),
      width: 150,
      editable: false,
      sortable: false,
      type: 'date',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<Date>) => {
        if (params.value !== null) {
          const dateValue = new Date(params.value);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {dateValue.getDate()}-{dateValue.getMonth() + 1}-{dateValue.getFullYear()}
            </Box>
          );
        }
        return <Box />;
      },
    },
  ];

  return (
    <WageIndexationHalfGridContainer>
      <Typography>{t(WAGE_INDEXATION__NEW_VALUES)}</Typography>
      <UPDataGrid
        rows={wages ?? []}
        columns={workerclassColumns}
        setRowOnHover={setRowOnHover}
        withCheckboxes={false}
      />
    </WageIndexationHalfGridContainer>
  );
};

export default IndexedWageValuesGrid;
